'use client'
// ═══════════════════════════════════════════════════════════════
// IMPERSONATION — "se connecter en tant que" (admin uniquement)
// ═══════════════════════════════════════════════════════════════
// Flux :
//   1. On sauvegarde la session admin courante (tokens) dans sessionStorage.
//   2. On demande au serveur un token_hash magiclink pour la cible.
//   3. On consomme ce token via verifyOtp → la session devient celle de la cible.
//   4. Pour revenir : on restaure les tokens admin sauvegardés (setSession).
import { supabase } from './supabase'
import { authFetch } from './auth-fetch'

const BACKUP_KEY = 'aureus_impersonation_backup'

/** Démarre l'impersonation d'un utilisateur cible. Recharge l'app en tant que lui. */
export async function startImpersonation(targetUserId, targetEmail) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Session admin introuvable — reconnectez-vous.')

  const res = await authFetch('/api/admin/impersonate', {
    method: 'POST',
    body: JSON.stringify({ target_user_id: targetUserId }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || !json.token_hash) {
    throw new Error(json.error || 'Échec de l’impersonation')
  }

  sessionStorage.setItem(BACKUP_KEY, JSON.stringify({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    admin_email: session.user?.email || '',
    target_email: targetEmail || json.email || '',
  }))

  const { error } = await supabase.auth.verifyOtp({ token_hash: json.token_hash, type: 'magiclink' })
  if (error) {
    sessionStorage.removeItem(BACKUP_KEY)
    throw error
  }

  window.location.href = '/'
}

/** Renvoie { admin_email, target_email } si une impersonation est en cours, sinon null. */
export function getImpersonationState() {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(BACKUP_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

/** Restaure la session admin et recharge l'app. */
export async function stopImpersonation() {
  let backup = null
  try { backup = JSON.parse(sessionStorage.getItem(BACKUP_KEY) || 'null') } catch { /* ignore */ }
  if (!backup?.access_token || !backup?.refresh_token) {
    sessionStorage.removeItem(BACKUP_KEY)
    await supabase.auth.signOut().catch(() => {})
    window.location.href = '/login'
    return
  }
  await supabase.auth.setSession({
    access_token: backup.access_token,
    refresh_token: backup.refresh_token,
  })
  sessionStorage.removeItem(BACKUP_KEY)
  window.location.href = '/'
}
